import { useAuth0 } from '@auth0/auth0-react'
import { AxiosError } from 'axios'
import { useEffect, useRef, useState } from 'react'
import { axiosInstance, axiosInstanceEntity } from '../services/axiosInstance'
import { updateUserSessionsAudit } from '../services/userServices'
import { USER_SESSION_AUDIT } from '../constants/constants'
import { useAppSelector } from '../redux/hook'

/** UBQ-5579: the services' answer to a user who may only use the Registry Tool. */
const REGISTRY_TOOL_ONLY = 'REGISTRY_TOOL_ONLY'

/** Auth0 error codes meaning the session cannot be refreshed any more. */
const SESSION_EXPIRED_ERRORS = new Set([
    'invalid_grant',
    'missing_refresh_token',
    'login_required',
])

const isSessionExpiredError = (error: unknown): boolean =>
    SESSION_EXPIRED_ERRORS.has((error as { error?: string })?.error ?? '')

/** Set once logout has started so concurrent requests don't repeat it. */
let isLoggingOut = false

const useAppInitiator = (): boolean => {
    const { isAuthenticated, getAccessTokenSilently, isLoading, logout } =
        useAuth0()
    const [accessTokenSaved, setAccessTokenSaved] = useState(false)
    const isGuestUser =
        useAppSelector((state) => state.searchParams.isGuestUser) === 'true'

    // Track interceptor IDs to prevent accumulation
    const interceptorRefs = useRef<{
        mainReq: number | null
        entityReq: number | null
        mainRes: number | null
        entityRes: number | null
    }>({ mainReq: null, entityReq: null, mainRes: null, entityRes: null })

    useEffect(() => {
        // Eject previous interceptors before registering new ones
        if (interceptorRefs.current.mainReq !== null) {
            axiosInstance.interceptors.request.eject(
                interceptorRefs.current.mainReq,
            )
        }
        if (interceptorRefs.current.entityReq !== null) {
            axiosInstanceEntity.interceptors.request.eject(
                interceptorRefs.current.entityReq,
            )
        }
        if (interceptorRefs.current.mainRes !== null) {
            axiosInstance.interceptors.response.eject(
                interceptorRefs.current.mainRes,
            )
        }
        if (interceptorRefs.current.entityRes !== null) {
            axiosInstanceEntity.interceptors.response.eject(
                interceptorRefs.current.entityRes,
            )
        }

        /**
         * Gets the access token for a request. When the session can no longer
         * be refreshed (refresh token expired/invalid) the user is logged out
         * once and the request is left pending, so no further /oauth/token
         * calls or error banners are produced while the login page loads.
         */
        const getTokenOrLogout = async (): Promise<string> => {
            try {
                return await getAccessTokenSilently()
            } catch (error) {
                if (isSessionExpiredError(error)) {
                    if (!isLoggingOut) {
                        isLoggingOut = true
                        logout({
                            logoutParams: { returnTo: window.location.origin },
                        })
                    }
                    return new Promise<string>(() => {})
                }
                throw error
            }
        }

        // --- Request interceptors: attach access token ---
        interceptorRefs.current.mainReq =
            axiosInstance.interceptors.request.use(async (request) => {
                const accessToken = await getTokenOrLogout()
                request.headers.Authorization = 'Bearer ' + accessToken
                return request
            })

        interceptorRefs.current.entityReq =
            axiosInstanceEntity.interceptors.request.use(
                async (request: any) => {
                    if (!request.isGuestUser) {
                        const accessToken = await getTokenOrLogout()
                        request.headers.Authorization = 'Bearer ' + accessToken
                    }
                    return request
                },
            )

        // --- Response interceptors: handle expired tokens ---
        const createResponseInterceptor = (instance: typeof axiosInstance) => {
            return instance.interceptors.response.use(
                (response) => response,
                async (error: AxiosError) => {
                    const originalRequest = error.config as any
                    /* UBQ-5579: a fresh token would be refused the same way, so no retry. */
                    if (
                        (error.response?.data as { code?: string } | undefined)
                            ?.code === REGISTRY_TOOL_ONLY
                    ) {
                        throw error
                    }
                    if (
                        (error.response?.status === 401 ||
                            error.response?.status === 403) &&
                        !originalRequest._retry
                    ) {
                        originalRequest._retry = true
                        try {
                            // Force a fresh token from Auth0 using the refresh token
                            const accessToken = await getAccessTokenSilently({
                                cacheMode: 'off',
                            })
                            originalRequest.headers.Authorization =
                                'Bearer ' + accessToken
                            return instance(originalRequest)
                        } catch (refreshError) {
                            // Refresh token is also expired — force logout
                            logout({
                                logoutParams: {
                                    returnTo: window.location.origin,
                                },
                            })
                            return Promise.reject(refreshError)
                        }
                    }
                    return Promise.reject(error)
                },
            )
        }

        interceptorRefs.current.mainRes =
            createResponseInterceptor(axiosInstance)
        interceptorRefs.current.entityRes =
            createResponseInterceptor(axiosInstanceEntity)

        const saveToken = async () => {
            try {
                // Use cached tokens — SDK will automatically refresh via
                // refresh token only when the access token is expired
                await getAccessTokenSilently()
                updateUserSessionsAudit(USER_SESSION_AUDIT.LOGIN)
                setAccessTokenSaved(true)
            } catch (error) {
                console.log(error)

                /**
                 * When refresh token expires, the API fails with 403 status code. The API response would be
                 *  {
                        "error": "invalid_grant",
                        "error_description": "Unknown or invalid refresh token."
                    }
                    So we are logging out the user and asking them to login again.
                 */
                logout({
                    logoutParams: {
                        returnTo: window.location.origin,
                    },
                })
            }
        }
        if (isAuthenticated) {
            if (!isGuestUser) {
                saveToken()
            } else {
                setAccessTokenSaved(true)
            }
        }

        // Cleanup interceptors on unmount
        return () => {
            if (interceptorRefs.current.mainReq !== null) {
                axiosInstance.interceptors.request.eject(
                    interceptorRefs.current.mainReq,
                )
            }
            if (interceptorRefs.current.entityReq !== null) {
                axiosInstanceEntity.interceptors.request.eject(
                    interceptorRefs.current.entityReq,
                )
            }
            if (interceptorRefs.current.mainRes !== null) {
                axiosInstance.interceptors.response.eject(
                    interceptorRefs.current.mainRes,
                )
            }
            if (interceptorRefs.current.entityRes !== null) {
                axiosInstanceEntity.interceptors.response.eject(
                    interceptorRefs.current.entityRes,
                )
            }
        }
    }, [isAuthenticated, getAccessTokenSilently, logout, isGuestUser])

    return isLoading || (isAuthenticated && !accessTokenSaved)
}

export default useAppInitiator
